// src/pages/ExperimentationAcademyPage.js - ORIGINAL LAYOUT + API
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import PageWrapper from '../components/PageWrapper';
import api from '../services/api';
import SafeImage from '../components/SafeImage';
import '../styles/pages/ExperimentationAcademyPage.css';

const ExperimentationAcademyPage = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    fetchPrograms();

    return () => {
      setLoading(true);
    };
  }, [location.pathname]);

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      const response = await api.getPrograms();

      if (response.success && response.data) {
        setPrograms(response.data);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
   <PageWrapper>
      <SEO
        title="Experimentation Academy | Supernova Lab"
        description="ARC Academy equips innovators, researchers and emerging founders with the mindset, tools and strategic capability to design and run mission-driven experiments"
        keywords="academy, programs, fellowship, training, education, Barbados"
      />

      <main className="academy-page">
        <motion.section
          className="academy-header-section"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="container">
            <div className="academy-badge">Shape</div>
            <h1 className="academy-main-title">
              <span className="animated-title-primary">Experimentation</span>
              <br />
              <span className="text-gradient-dark">Academy</span>
            </h1>
          </div>
        </motion.section>

        <motion.section
          className="arc-academy-section py-5"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="container">
            <div className="text-center mb-5">
              <h2 className="arc-academy-title">ARC Academy</h2>
              <p className="arc-academy-description">
                ARC Academy equips innovators, researchers and emerging<br />
                founders with the mindset, tools and strategic capability to<br />
                design and run mission-driven experiments.
              </p>
              <p className="arc-academy-description mt-4">
                Through foresight retreats, policy sprints and capacity-building<br />
                fellowships, the Academy develops the talent and readiness<br />
                needed to move from early signals to tested national missions.
              </p>
            </div>
          </div>
          <div className='d-flex align-items-center flex-column'>
            <button className="join-academy-btn">
              <a href="https://the-arc-academy.mn.co/landing" target="_blank" rel="noopener noreferrer" className="text-decoration-none text-white">
                Join the Academy
              </a>
              <i className="fas fa-arrow-right ms-2"></i>
            </button>
          </div>
        </motion.section>

        <section className="academy-programs-section py-5">
          <div className="container-fluid px-0">
            {loading ? (
              <div className="text-center py-5">
                <i className="fas fa-spinner fa-spin fa-3x"></i>
                <p className="mt-3">Loading programs...</p>
              </div>
            ) : programs.length > 0 ? (
              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                {programs.map((program) => (
                  <motion.div key={program.id} className="academy-program-block" variants={itemVariants}>
                    <div className="program-bg-image">
                      <SafeImage
                        src={program.image}
                        alt={program.title}
                        className="program-bg-image img"
                        placeholderText="Project"
                      />
                    </div>
                    <div className="program-content d-flex align-items-center flex-column ">
                      <h3 className="program-title">{program.title}</h3>
                      <p className="program-description">{program.description}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <div className="text-center py-5">
                <p className="text-muted">No programs available at the moment</p>
              </div>
            )}

            <motion.div
              className="text-center py-5"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <button className="join-academy-btn">
                <a href="https://the-arc-academy.mn.co/landing" target="_blank" rel="noopener noreferrer" className="text-decoration-none text-white">
                  Join the Academy
                </a>
                <i className="fas fa-arrow-right ms-2"></i>
              </button>
            </motion.div>
          </div>
        </section>
      </main>
   </PageWrapper>
  );
};

export default ExperimentationAcademyPage;